// src/components/site-editor/SiteEditor.jsx
import React, { useState, useCallback } from 'react';
import { useSiteEditor } from '../../hooks/useSiteEditor';
import SiteEditorPreview from './SiteEditorPreview';
import SiteEditorPanel from './SiteEditorPanel';
import { Loader2 } from 'lucide-react';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle
} from '../ui/resizable';

/**
 * Site Editor Main Component
 *
 * Allows editing client site content with live preview
 * Uses ResizablePanel pattern like the email builder
 */
const SiteEditor = () => {
  const {
    currentSite,
    loading,
    error,
    isConnected
  } = useSiteEditor();

  // Panel size persistence
  const [editorPanelSize, setEditorPanelSize] = useState(() => {
    const saved = localStorage.getItem('site-editor-panel-size');
    return saved ? parseFloat(saved) : 25; // Default 25%
  });

  // Handle panel resize
  const handleLayoutChange = useCallback((sizes) => {
    if (sizes.length === 2) {
      const newPanelSize = sizes[1]; // Right panel
      setEditorPanelSize(newPanelSize);
      localStorage.setItem('site-editor-panel-size', newPanelSize.toString());
    }
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <p className="text-muted-foreground">Loading site editor...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error</h2>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  // Not connected
  if (!isConnected) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Not Connected</h2>
          <p className="text-muted-foreground">Connecting to server...</p>
        </div>
      </div>
    );
  }

  // No site found
  if (!currentSite) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">No Sites Found</h2>
          <p className="text-muted-foreground">
            No client sites available. Create one first.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="site-editor h-screen flex flex-col">
      {/* Main Content - Resizable Panels */}
      <div className="flex-1 overflow-hidden">
        <ResizablePanelGroup
          direction="horizontal"
          className="h-full"
          onLayout={handleLayoutChange}
        >
          {/* Left Panel - Preview */}
          <ResizablePanel
            defaultSize={100 - editorPanelSize}
            minSize={50}
          >
            <div className="h-full bg-gray-100">
              <SiteEditorPreview />
            </div>
          </ResizablePanel>

          {/* Draggable Handle */}
          <ResizableHandle withHandle />

          {/* Right Panel - Editor */}
          <ResizablePanel
            defaultSize={editorPanelSize}
            minSize={22}
            maxSize={50}
          >
            <SiteEditorPanel />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
};

export default SiteEditor;
